/*
 * Which palette is active, and the one piece of theming CSS can't do alone.
 *
 * THE SEAM THIS FILE EXISTS TO CLOSE
 *
 * Swapping palettes is free for anything styled with tokens: change the class
 * on the root and every `var(--c-*)` re-resolves. But WoW class colors, WCL
 * parse colors and DB-driven role colors arrive at runtime as raw hex, and
 * they're authored for a dark UI. On a light background several are illegible
 * (Priest is #FFFFFF), so tokens.ts/onLight darkens them just enough to read.
 *
 * On a DARK background that fixup is not just unnecessary, it's wrong — the
 * canonical colors are already correct and adjusting them would distort a
 * convention players read at a glance.
 *
 * So the adjustment has to know the active palette. Components pull it from
 * here instead of importing onLight directly, and it becomes identity on dark.
 *
 * ADDING A THEME
 *   1. Add a `.lfm-<name>` block to themes.css
 *   2. Add the name to THEMES below, with `adjustsForLightBg` set correctly
 *   That's it — no component changes.
 */
import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { onLight, onLightAccent } from "./tokens";
import "./themes.css";

export type ThemeName = "calm" | "dark";

type ThemeDef = {
  className: string;
  label: string;
  /*
   * Do runtime hex colors (class/role/parse) need darkening to stay legible?
   * True for light palettes, false for dark ones, where the canonical
   * colors are already correct and must be left alone.
   */
  adjustsForLightBg: boolean;
};

export const THEMES: Record<ThemeName, ThemeDef> = {
  calm: { className: "lfm-calm", label: "Calm", adjustsForLightBg: true },
  dark: { className: "lfm-dark", label: "Dark", adjustsForLightBg: false },
};

// One choice for the whole site: themed pages are rolled out one at a time and
// a player should not have to pick light or dark again on each of them. The old
// per-page key is still read so nobody's earlier choice is lost.
const STORAGE_KEY = "lfm_theme";
const LEGACY_STORAGE_KEY = "lfm_theme_create_raid";
const DEFAULT_THEME: ThemeName = "calm";

function readStoredTheme(): ThemeName {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY) ?? window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (v && v in THEMES) return v as ThemeName;
  } catch {
    /* private mode / storage disabled — fall through to the default */
  }
  return DEFAULT_THEME;
}

/* Identity pair, used when the palette needs no runtime fixup. */
const asIs = (hex: string) => hex;

type ThemeContextValue = {
  theme: ThemeName;
  setTheme: (t: ThemeName) => void;
  toggleTheme: () => void;
  /* `lfm-theme` (shared behaviour) + the active palette class. */
  themeClass: string;
  /* Legibility fixups. Identity on dark palettes. See the note above. */
  onLight: (hex: string) => string;
  onLightAccent: (hex: string) => string;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({
  children,
  initial,
}: {
  children: React.ReactNode;
  initial?: ThemeName;
}) {
  // A themed page can host another themed page (the planner renders Create
  // Raid inside itself). The inner provider must not start a second, separate
  // theme state, or the two toggles would disagree.
  const parent = useContext(ThemeContext);
  if (parent) return <>{children}</>;
  return <RootThemeProvider initial={initial}>{children}</RootThemeProvider>;
}

function RootThemeProvider({
  children,
  initial,
}: {
  children: React.ReactNode;
  initial?: ThemeName;
}) {
  const [theme, setThemeState] = useState<ThemeName>(() => initial ?? readStoredTheme());

  const setTheme = useCallback((t: ThemeName) => {
    setThemeState(t);
    try {
      window.localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* not worth failing a render over */
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: ThemeName = prev === "calm" ? "dark" : "calm";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(() => {
    const def = THEMES[theme] ?? THEMES[DEFAULT_THEME];
    return {
      theme,
      setTheme,
      toggleTheme,
      themeClass: `lfm-theme ${def.className}`,
      onLight: def.adjustsForLightBg ? onLight : asIs,
      onLightAccent: def.adjustsForLightBg ? onLightAccent : asIs,
    };
  }, [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/*
 * Falls back to the default palette rather than throwing, so a themed
 * component dropped outside a provider still renders something sane.
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  const def = THEMES[DEFAULT_THEME];
  return {
    theme: DEFAULT_THEME,
    setTheme: () => {},
    toggleTheme: () => {},
    themeClass: `lfm-theme ${def.className}`,
    onLight: def.adjustsForLightBg ? onLight : asIs,
    onLightAccent: def.adjustsForLightBg ? onLightAccent : asIs,
  };
}
